'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { CheckCircle2, Loader2 } from 'lucide-react';

interface ContactValues {
  name: string;
  email: string;
  message: string;
}

export default function ContactForm() {
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactValues>({ defaultValues: { name: '', email: '', message: '' } });

  const onSubmit = async () => {
    setSubmitting(true);
    // TODO: send enquiry to backend once connected.
    await new Promise((r) => setTimeout(r, 700));
    setSubmitting(false);
    setSent(true);
    reset();
  };

  if (sent) {
    return (
      <div className="card-surface flex flex-col items-center p-8 text-center">
        <CheckCircle2 size={36} className="text-success" />
        <h2 className="mt-3 text-xl font-bold text-card-foreground">Thank you for your message</h2>
        <p className="mt-2 text-sm text-muted-foreground">The CBS team will be in touch shortly.</p>
        <button type="button" onClick={() => setSent(false)} className="btn-outline mt-5">Send another message</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="card-surface space-y-4 p-6 sm:p-8">
      <div>
        <label htmlFor="contact-name" className="mb-1.5 block text-sm font-semibold text-foreground">Full name</label>
        <input id="contact-name" type="text" autoComplete="name" className={`field-input ${errors.name ? 'field-input-error' : ''}`} {...register('name', { required: 'Enter your name' })} />
        {errors.name && <p className="mt-1.5 text-xs font-medium text-destructive">{errors.name.message}</p>}
      </div>
      <div>
        <label htmlFor="contact-email" className="mb-1.5 block text-sm font-semibold text-foreground">Email address</label>
        <input
          id="contact-email"
          type="email"
          autoComplete="email"
          className={`field-input ${errors.email ? 'field-input-error' : ''}`}
          {...register('email', {
            required: 'Enter your email address',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && <p className="mt-1.5 text-xs font-medium text-destructive">{errors.email.message}</p>}
      </div>
      <div>
        <label htmlFor="contact-message" className="mb-1.5 block text-sm font-semibold text-foreground">How can we help?</label>
        <textarea id="contact-message" rows={5} className={`field-input ${errors.message ? 'field-input-error' : ''}`} {...register('message', { required: 'Enter your message' })} />
        {errors.message && <p className="mt-1.5 text-xs font-medium text-destructive">{errors.message.message}</p>}
      </div>
      <button type="submit" disabled={submitting} className="btn-primary w-full !py-3">
        {submitting ? (<><Loader2 size={17} className="animate-spin" /> Sending…</>) : 'Send message'}
      </button>
    </form>
  );
}
